/**
 * AI Vehicle Diagnostic Component
 * VIN lookup, work instructions, parts search with nearby stores
 */

import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Car, Wrench, AlertTriangle, CheckCircle2,
  Search, MapPin, Phone, Clock, Package, ChevronDown, ChevronUp,
  FileText, Shield, Info, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service categories
const serviceCategories = [
  { id: 'engine', label: 'Engine', icon: '🔧' },
  { id: 'transmission', label: 'Transmission', icon: '⚙️' },
  { id: 'brakes', label: 'Brakes', icon: '🛑' },
  { id: 'electrical', label: 'Electrical', icon: '⚡' },
  { id: 'suspension', label: 'Suspension', icon: '🚗' },
  { id: 'cooling', label: 'Cooling', icon: '❄️' },
  { id: 'exhaust', label: 'Exhaust', icon: '💨' },
  { id: 'fuel', label: 'Fuel System', icon: '⛽' }
];

export default function AIVehicleDiagnostic({ onDiagnosticComplete, initialVehicle, forProvider = false }) {
  const { getAuthHeaders } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Vehicle Info, 2: Problem Description, 3: Results
  const [loading, setLoading] = useState(false);
  const [searchingParts, setSearchingParts] = useState(false);
  const [showVinEntry, setShowVinEntry] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showParts, setShowParts] = useState(true);
  const [showStores, setShowStores] = useState(false);
  
  const [vehicleInfo, setVehicleInfo] = useState({
    vin: '',
    make: initialVehicle?.make || '',
    model: initialVehicle?.model || '',
    year: initialVehicle?.year || new Date().getFullYear()
  });
  
  const [problemInfo, setProblemInfo] = useState({
    category: '',
    description: '',
    symptoms: []
  });
  
  const [diagnostic, setDiagnostic] = useState(null);
  const [partsResults, setPartsResults] = useState(null);
  
  // Decode VIN
  const decodeVin = async () => {
    if (!vehicleInfo.vin || vehicleInfo.vin.length !== 17) {
      toast.error('VIN must be exactly 17 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/vehicle/decode-vin`,
        { vin: vehicleInfo.vin },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      if (response.data.valid) {
        setVehicleInfo(prev => ({
          ...prev,
          make: response.data.manufacturer?.split(' ')[0] || prev.make,
          year: response.data.model_year || prev.year
        }));
        toast.success('VIN decoded successfully!');
      }
    } catch (error) {
      toast.error('Could not decode VIN. Please enter vehicle details manually.');
    } finally {
      setLoading(false);
    }
  };
  
  // Get AI diagnostic
  const getDiagnostic = async () => {
    if (!vehicleInfo.make || !vehicleInfo.model || !problemInfo.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/vehicle/diagnostic`,
        {
          vin: vehicleInfo.vin || null,
          make: vehicleInfo.make,
          model: vehicleInfo.model,
          year: vehicleInfo.year,
          problem_description: problemInfo.description,
          service_category: problemInfo.category || 'general',
          symptoms: problemInfo.symptoms
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setDiagnostic(response.data);
      setStep(3);
      
      // Auto-search for parts based on likely components
      if (response.data.instructions?.likely_components?.length > 0) {
        searchForParts(response.data.instructions.likely_components[0]);
      }
      
      if (onDiagnosticComplete) {
        onDiagnosticComplete(response.data);
      }
    } catch (error) {
      toast.error('Failed to generate diagnostic. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Search for parts
  const searchForParts = async (partName) => {
    setSearchingParts(true);
    try {
      const response = await axios.post(
        `${API}/vehicle/search-parts`,
        {
          part_name: partName,
          vehicle_make: vehicleInfo.make,
          vehicle_model: vehicleInfo.model,
          vehicle_year: vehicleInfo.year
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setPartsResults(response.data);
    } catch (error) {
      console.error('Parts search failed:', error);
    } finally {
      setSearchingParts(false);
    }
  };
  
  const instructions = diagnostic?.instructions;

  return (
    <Card className="w-full" data-testid="ai-vehicle-diagnostic">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl">AI Vehicle Diagnostic</CardTitle>
            <CardDescription>
              {forProvider ? 'Generate work instructions for this repair' : 'Get detailed repair analysis'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Step 1: Vehicle Info */}
        {step === 1 && (
          <>
            {/* VIN Entry */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <Label className="font-medium">Enter VIN for automatic vehicle detection</Label>
                <button
                  onClick={() => setShowVinEntry(!showVinEntry)}
                  className="text-blue-600 text-sm"
                >
                  {showVinEntry ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showVinEntry && (
                <div className="flex gap-2">
                  <Input
                    value={vehicleInfo.vin}
                    onChange={(e) => setVehicleInfo(prev => ({ ...prev, vin: e.target.value.toUpperCase() }))}
                    placeholder="Enter 17-character VIN"
                    maxLength={17}
                    className="font-mono"
                    data-testid="vin-input"
                  />
                  <Button
                    onClick={decodeVin}
                    disabled={loading || vehicleInfo.vin.length !== 17}
                    variant="outline"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Decode'}
                  </Button>
                </div>
              )}
            </div>

            {/* Manual Entry */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  value={vehicleInfo.make}
                  onChange={(e) => setVehicleInfo(prev => ({ ...prev, make: e.target.value }))}
                  placeholder="Toyota"
                  className="mt-1"
                  data-testid="make-input"
                />
              </div>
              <div>
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  value={vehicleInfo.model}
                  onChange={(e) => setVehicleInfo(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Camry"
                  className="mt-1"
                  data-testid="model-input"
                />
              </div>
              <div>
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  value={vehicleInfo.year}
                  onChange={(e) => setVehicleInfo(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  className="mt-1"
                  data-testid="year-input"
                />
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!vehicleInfo.make || !vehicleInfo.model}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
              data-testid="continue-to-problem-btn"
            >
              Continue to Problem Description
            </Button>
          </>
        )}

        {/* Step 2: Problem Description */}
        {step === 2 && (
          <>
            {/* Vehicle Summary */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Car className="w-5 h-5 text-gray-600" />
              <span className="font-medium">
                {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
              </span>
              <button
                onClick={() => setStep(1)}
                className="ml-auto text-sm text-blue-600 hover:underline"
              >
                Edit
              </button>
            </div>

            {/* Category Selection */}
            <div>
              <Label className="mb-2 block">What system is affected? (Optional)</Label>
              <div className="grid grid-cols-4 gap-2">
                {serviceCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setProblemInfo(prev => ({ ...prev, category: cat.id }))}
                    className={`p-2 border rounded-lg text-center transition-all text-sm ${
                      problemInfo.category === cat.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <span className="text-lg block">{cat.icon}</span>
                    <span className="text-xs">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Problem Description */}
            <div>
              <Label htmlFor="problem">Describe the problem in detail *</Label>
              <Textarea
                id="problem"
                value={problemInfo.description}
                onChange={(e) => setProblemInfo(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Example: My car makes a grinding noise when I press the brakes. The noise is louder when braking at high speeds. The brake pedal also feels soft and requires more pressure than usual..."
                className="mt-1 min-h-[120px]"
                data-testid="problem-description"
              />
              <p className="text-xs text-gray-500 mt-1">
                The more detail you provide, the more accurate the diagnosis will be.
              </p>
            </div>

            {/* Important Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800">Important Notice</p>
                <p className="text-amber-700 mt-1">
                  This AI diagnosis is for informational purposes. <strong>DO NOT PURCHASE ANY PARTS</strong> until 
                  a certified technician has confirmed the diagnosis through hands-on inspection.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-12"
              >
                Back
              </Button>
              <Button
                onClick={getDiagnostic}
                disabled={loading || !problemInfo.description}
                className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-purple-600"
                data-testid="get-diagnostic-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get AI Diagnosis
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Step 3: Results */}
        {step === 3 && instructions && (
          <div className="space-y-4">
            {/* Vehicle & Problem Summary */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Car className="w-5 h-5 text-blue-600" />
                <span className="font-semibold">
                  {instructions.vehicle.year} {instructions.vehicle.make} {instructions.vehicle.model}
                </span>
                {instructions.vehicle.vin && (
                  <Badge variant="outline" className="text-xs">VIN: {instructions.vehicle.vin}</Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">{instructions.problem_summary}</p>
            </div>

            {/* Estimated Time & Difficulty */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <Clock className="w-4 h-4" />
                  Estimated Time
                </div>
                <p className="font-semibold">{instructions.estimated_time}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <Wrench className="w-4 h-4" />
                  Difficulty
                </div>
                <p className="font-semibold">{instructions.difficulty}</p>
              </div>
            </div>

            {/* Safety Warnings */}
            {instructions.safety_warnings?.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-800">Safety Warnings</span>
                </div>
                <ul className="text-sm text-red-700 space-y-1">
                  {instructions.safety_warnings.map((warning, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <AlertTriangle className="w-3 h-3 mt-1 flex-shrink-0" />
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Work Instructions */}
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setShowInstructions(!showInstructions)}
                className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">Step-by-Step Work Instructions</span>
                </div>
                {showInstructions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showInstructions && (
                <div className="p-4 space-y-4">
                  {/* Diagnostic Steps */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Diagnostic Steps:</h4>
                    <ol className="space-y-2">
                      {instructions.diagnostic_steps?.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                            {idx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Repair Steps */}
                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-gray-900 mb-2">Repair Procedure:</h4>
                    <ol className="space-y-2">
                      {instructions.repair_steps?.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                            {idx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}
            </div>

            {/* Parts Section */}
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setShowParts(!showParts)}
                className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold">Likely Parts Needed</span>
                  <Badge variant="outline" className="ml-2">
                    {instructions.likely_components?.length || 0} items
                  </Badge>
                </div>
                {showParts ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showParts && (
                <div className="p-4">
                  {/* Important Notice */}
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg mb-4">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>{instructions.important_notice}</strong>
                    </p>
                  </div>

                  {/* Likely Components */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {instructions.likely_components?.map((component, idx) => (
                      <button
                        key={idx}
                        onClick={() => searchForParts(component)}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-blue-100 rounded-full text-sm transition-colors"
                      >
                        {component}
                        <Search className="w-3 h-3 inline ml-1" />
                      </button>
                    ))}
                  </div>

                  {/* Parts Search Results */}
                  {searchingParts && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Searching marketplace...
                    </div>
                  )}

                  {partsResults && !searchingParts && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Searching for: <strong>{partsResults.search_term}</strong>
                      </p>

                      {partsResults.marketplace_results?.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-green-600">
                            <CheckCircle2 className="w-4 h-4 inline mr-1" />
                            Found in Jiffys Marketplace:
                          </p>
                          {partsResults.marketplace_results.map((product, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                              <div>
                                <p className="font-medium">{product.title}</p>
                                <p className="text-sm text-gray-600">Seller: {product.seller_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600">${product.price}</p>
                                <Badge variant={product.compatible ? "default" : "secondary"}>
                                  {product.compatible ? 'Compatible' : 'Check Fit'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                          <p className="text-xs text-gray-500 italic">
                            {partsResults.auto_add_disclaimer}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No matching parts in marketplace</p>
                      )}

                      {/* Nearby Stores */}
                      <div className="pt-3 border-t">
                        <button
                          onClick={() => setShowStores(!showStores)}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                        >
                          <MapPin className="w-4 h-4" />
                          {showStores ? 'Hide' : 'Show'} nearby auto parts stores
                        </button>
                        
                        {showStores && partsResults.nearby_carriers && (
                          <div className="mt-3 space-y-2">
                            {partsResults.nearby_carriers.map((store, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                  <p className="font-medium">{store.name}</p>
                                  <p className="text-sm text-gray-600">{store.address}</p>
                                  <p className="text-xs text-gray-500">
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {store.hours}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium">{store.distance} mi</p>
                                  <a
                                    href={`tel:${store.phone}`}
                                    className="text-blue-600 text-sm flex items-center gap-1"
                                  >
                                    <Phone className="w-3 h-3" />
                                    Call
                                  </a>
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 italic">
                              {partsResults.disclaimer || 'Call ahead to confirm availability'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setStep(1);
                  setDiagnostic(null);
                  setPartsResults(null);
                }}
                className="flex-1"
              >
                New Diagnosis
              </Button>
              {forProvider && (
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => onDiagnosticComplete && onDiagnosticComplete(diagnostic)}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirm Diagnosis
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
